import { Link, useParams, useSearchParams } from 'react-router-dom'
import {
  courseSlug,
  lectureCitation,
  lectureLabel,
  matchesLectureRef,
  minutesToClock,
  weekdayName,
  weekdayOf,
  type Course,
  type Lecture,
} from '@app/shared'

import {
  useClock,
  useCourse,
  useFlashcards,
  useLectureTranscript,
  useLectures,
  usePracticeQuestions,
  useLectureSummary,
  useSyncStatus,
} from '../lib/data'
import { DataModeBadge } from '../components/HeaderBand'
import { SkeletonRows } from '../components/Card'
import { Monitor } from '../components/school/icons'
import {
  Breadcrumb,
  CanvasSyncChip,
  LectureStatusChip,
  SchoolBand,
  SchoolSearchField,
  Unavailable,
} from '../components/school/Chrome'
import { TranscriptPanel } from '../components/school/Transcript'
import { FlashcardsCard, PracticeCard, SummaryCard } from '../components/school/StudySet'
import { durationMinutes, hueBg } from '../components/school/derive'

/**
 * LECTURE PAGE · STUDY MODE — docs/design/handoff/"LifeOS School.dc.html", option 6c.
 *
 * The searchable timestamped transcript on the left; the summary, flashcards and practice
 * questions on the right, each carrying a provenance label naming what wrote it and from what.
 *
 * WHERE THE AUDIO IS. The recording lives on the owner's desktop PC, archived after the worker
 * transcribed it — the cloud copy is deleted on purpose (CLAUDE.md → Architecture). The page
 * says so in the header chip ('audio on desktop') and again in the transcript's banner, and the
 * playback affordances are visibly inert rather than silently dead. A study screen that pretends
 * to stream a file sitting on a sleeping machine in Berkeley is the kind of small lie that costs
 * ten minutes at exactly the wrong moment.
 *
 * `?q=` seeds the transcript search, so a hit from `/search` can land on the phrase rather than
 * at the top of a 78-minute lecture.
 */
export function SchoolLecture() {
  const { courseCode, n } = useParams<{ courseCode: string; n: string }>()
  const [params] = useSearchParams()
  const { data: clock } = useClock()
  const { data: course, isPending: coursePending, error: courseError } = useCourse(courseCode)
  const { data: lectures, isPending: lecturesPending } = useLectures(course?.id)
  const { data: sync, error: syncError } = useSyncStatus()

  // Matched by REF, not by number: an unnumbered recording — which is most of them, since
  // nothing in the pipeline assigns a lecture number — is addressed by its own id instead
  // (packages/shared/src/school.ts → lectureRef).
  const lecture = (lectures ?? []).find((l) => matchesLectureRef(l, n ?? ''))

  const {
    data: transcript,
    isPending: transcriptPending,
    error: transcriptError,
  } = useLectureTranscript(lecture?.status === 'transcribed' ? lecture.id : undefined)
  const {
    data: summary,
    isPending: summaryPending,
    error: summaryError,
  } = useLectureSummary(lecture?.id)
  const { data: flashcards, isPending: flashcardsPending, error: flashcardsError } = useFlashcards(
    lecture?.id,
  )
  const {
    data: practice,
    isPending: practicePending,
    error: practiceError,
  } = usePracticeQuestions(lecture?.id)

  const today = clock?.date
  const nowMinutes = clock?.minutesIntoDay ?? 0
  const title = lecture ? lectureLabel(lecture) : 'Lecture'

  return (
    <>
      <SchoolBand>
        <Breadcrumb
          to={`/school/${courseCode ?? ''}`}
          parent={course?.code ?? courseCode ?? 'Course'}
          title={lecture ? title : 'Lecture'}
        />
        <SchoolSearchField placeholder="Search this course" scope={course?.code} maxWidth={300} />
        {today && (
          <CanvasSyncChip rows={sync} error={syncError} today={today} nowMinutes={nowMinutes} />
        )}
        <DataModeBadge />
      </SchoolBand>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden px-[30px] pb-[26px]">
        {(coursePending || lecturesPending) && (
          <div className="rounded-context border border-line bg-surface px-5 py-[18px] shadow-card">
            <SkeletonRows rows={2} />
          </div>
        )}
        <Unavailable error={courseError} />

        {course && !lecturesPending && !lecture && (
          <div className="rounded-context border border-line bg-surface px-5 py-[18px] shadow-card">
            <p className="text-body text-ink-3 text-pretty">
              {/* "No lecture answers to this address" — true whether the address was a number or
                  an id, and it never claims to know why. */}
              {course.code} has no lecture at “{n}”. It may not have been held yet, or the
              recording may not be filed under this course.{' '}
              <Link to={`/school/${courseSlug(course.code)}`} className="underline underline-offset-2">
                Back to {course.code}
              </Link>
              .
            </p>
          </div>
        )}

        {course && lecture && today && (
          <>
            <LectureHeader course={course} lecture={lecture} />

            {lecture.status !== 'transcribed' ? (
              // NOT A BLANK STUDY SCREEN. A lecture still in the pipeline has nothing to study,
              // and saying which stage it is at is the difference between "waiting" and "broken".
              <div className="rounded-context border border-line bg-surface px-5 py-[18px] shadow-card">
                <p className="max-w-[620px] text-body text-ink-3 text-pretty">
                  {PIPELINE_SENTENCE[lecture.status]}
                </p>
              </div>
            ) : (
              <div className="flex min-h-0 flex-1 gap-3 overflow-hidden">
                <TranscriptPanel
                  transcript={transcript?.transcript}
                  segments={transcript?.segments}
                  isPending={transcriptPending}
                  error={transcriptError}
                  initialQuery={params.get('q') ?? ''}
                />

                {/* Three cards at their natural height, and the column scrolls if a viewport is
                    short. NOT a fixed column with a squashed card at the bottom: a practice
                    question crushed into a 40px scroller is worse than one you scroll to. */}
                <div className="flex w-[400px] min-h-0 flex-none flex-col gap-[14px] overflow-y-auto">
                  <SummaryCard
                    summary={summary}
                    lectureId={lecture.id}
                    isPending={summaryPending}
                    error={summaryError}
                  />
                  <FlashcardsCard
                    flashcards={flashcards}
                    isPending={flashcardsPending}
                    error={flashcardsError}
                  />
                  <PracticeCard
                    questions={practice}
                    isPending={practicePending}
                    error={practiceError}
                    lectureLabel={lectureCitation(lecture)}
                    courseCode={course.code}
                    courseId={course.id}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}

/** What each unfinished pipeline state actually means, said in the owner's terms. */
const PIPELINE_SENTENCE: Record<Lecture['status'], string> = {
  not_recorded:
    'This lecture was not recorded, so there is no transcript and no study set. Nothing failed — the recorder was simply not running.',
  audio_uploaded:
    'The audio is uploaded and queued. It transcribes the next time the desktop PC is awake, and the transcript and study set appear here when it does.',
  processing:
    'The desktop PC started transcribing this lecture and has not reported back. The audio is safe; the job runs again when the machine wakes up.',
  transcribed: '',
  failed:
    'Transcription failed after three attempts. The audio is still safe on the desktop PC; the error is on the Connections screen in plain words.',
}

function LectureHeader({ course, lecture }: { course: Course; lecture: Lecture }) {
  // The time and room come from the course's own meeting on that weekday — stored once, on the
  // course, rather than copied onto every lecture where it could drift.
  const meeting = course.meetings.find(
    (m) => m.kind === 'lecture' && m.weekday === weekdayOf(lecture.date),
  )
  const duration = durationMinutes(lecture.durationSec)

  return (
    <section className="flex flex-none items-center gap-[14px] rounded-context border border-line bg-surface px-[19px] py-[15px] shadow-card">
      <span
        className={['w-[5px] flex-none self-stretch rounded-[3px]', hueBg(course.code)].join(' ')}
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <h1 className="text-[17px] font-semibold leading-[1.1] tracking-[-0.01em] text-pretty">
          {/* The recording's own name when it has one; its number, or the day it happened, when
              it does not. Never a title invented for a class nobody named. */}
          {lectureLabel(lecture)}
        </h1>
        <div className="mt-[6px] flex flex-wrap items-center gap-[9px] text-[12px] font-normal leading-none text-muted">
          <span>
            {weekdayName(lecture.date, true)} {shortDate(lecture.date)}
            {meeting && ` · ${minutesToClock(meeting.startMinutes)}`}
          </span>
          {duration && <Dot />}
          {duration && <span>{duration}</span>}
          {meeting && <Dot />}
          {meeting && <span>{meeting.location}</span>}
        </div>
      </div>

      <div className="flex flex-none items-center gap-2">
        <LectureStatusChip status={lecture.status} />
        {lecture.audioLocation === 'archived_on_pc' && (
          <span
            className="flex items-center gap-[6px] whitespace-nowrap rounded-pill bg-surface-4 px-[9px] py-1 text-[10.5px] font-semibold leading-none text-muted"
            title="The original recording is archived on your desktop PC. The cloud copy was deleted after transcription, on purpose."
          >
            <Monitor size={11} className="text-microcopy" />
            audio on desktop
          </span>
        )}
        {lecture.audioLocation === 'cloud' && (
          <span
            className="flex items-center gap-[6px] whitespace-nowrap rounded-pill bg-surface-4 px-[9px] py-1 text-[10.5px] font-semibold leading-none text-muted"
            title="The recording is still in cloud storage waiting for the desktop worker. It moves to the PC and is deleted here once transcribed."
          >
            <Monitor size={11} className="text-microcopy" />
            audio waiting to transfer
          </span>
        )}
      </div>
    </section>
  )
}

function Dot() {
  return <span className="size-[3px] flex-none rounded-full bg-dash" aria-hidden />
}

/** 'Sep 10' beside a weekday the caller already printed. */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
function shortDate(date: string): string {
  const [, month, day] = date.split('-')
  return `${MONTHS[Number(month) - 1]} ${Number(day)}`
}
